import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn, cnState } from "@beweegbaas/ui/lib/utils";

function CheckIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      viewBox="0 0 18 18"
      aria-hidden="true"
      className="checkbox-icon-svg group-not-data-checked/checkbox:opacity-0"
      {...props}
    >
      <polyline points="2 9 7 14 16 4"></polyline>
    </svg>
  );
}

function HorizontalRuleIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentcolor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <line
        x1="3"
        y1="12"
        x2="21"
        y2="12"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
}

function Checkbox({ className, ...props }: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      // Without a `name`, Base UI pins the hidden <input> (which carries our `id`) at
      // position:fixed top/left 0 — inside a transformed ancestor (drawers/dialogs) that's the
      // panel's corner, so clicking an htmlFor label scroll-jumps to it. A `name` switches the
      // input to position:absolute beside the control, keeping label clicks scroll-neutral.
      name={props.name ?? props.id}
      className={cnState(
        [
          "indicator",
          "squircle relative supports-squircle:rounded-2xl",
          `group/checkbox border-input dark:bg-input/30
          data-checked:text-primary-foreground data-checked:border-primary
          aria-invalid:aria-checked:border-primary aria-invalid:border-destructive
          dark:aria-invalid:border-destructive/50 aria-invalid:ring-destructive/20
          dark:aria-invalid:ring-destructive/40 peer
          relative flex
          size-5 shrink-0 items-center justify-center rounded-[4px] border
          border-transparent after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed
          disabled:opacity-50 aria-invalid:ring-[3px] data-checked:bg-linear-to-b
          data-checked:from-blue-500 data-checked:to-blue-600 data-disabled:bg-black/20
          data-disabled:opacity-50`,
          //"group-has-disabled/field:opacity-50",
          focusStyles,
        ],
        className,
      )}
      {...props}
    >
      <span
        className="squircle absolute inset-0 transition-shadow
          supports-squircle:rounded-full"
      />

      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className={cn(`checkbox grid place-content-center text-current transition-none
          [&>svg]:size-3.5`)}
        render={(indicatorProps, state) => (
          <span {...indicatorProps}>
            {state.indeterminate ? (
              <HorizontalRuleIcon className="size-3 opacity-50" />
            ) : (
              <CheckIcon />
            )}
          </span>
        )}
      />
    </CheckboxPrimitive.Root>
  );
}

// Export CheckboxPrimitive for advanced use cases that need render props
export { Checkbox, CheckboxPrimitive };
